/**
 * Seeded pseudo-random generation for the parametric bootstrap
 * (brief 9.12, MET-11).
 *
 * The brief requires a seeded generator with independent, reproducible
 * substreams per cell and replicate, and identical results for any
 * number of workers. The design here is counter-based:
 *
 *   - The run seed (an arbitrary string at the engine interface) is
 *     hashed with FNV-1a (32-bit) over its UTF-16 code units.
 *   - The generator state for replicate b is derived from
 *     (seedHash, b) alone, by splitmix32 expansion — never from a
 *     sequentially advanced stream. A worker can therefore start at
 *     any replicate without replaying earlier ones, and the values a
 *     replicate consumes depend neither on the worker nor on how the
 *     replicates were partitioned into chunks.
 *   - The generator itself is xoshiro128** (Blackman and Vigna),
 *     seeded from the splitmix32 expansion as its authors recommend.
 *     All arithmetic is 32-bit integer arithmetic (Math.imul, shifts,
 *     rotations), which is exact and identical in every ECMAScript
 *     engine, so streams are bit-identical across platforms.
 *
 * Normal deviates use the inversion method: one uniform per deviate,
 * mapped through the module's inverse normal CDF (Acklam's rational
 * approximation, see distributions.ts, absolute error about 1e-9).
 * Uniforms are taken as (u32 + 0.5) / 2^32 so the quantile argument
 * always lies strictly inside (0, 1). The fixed consumption pattern —
 * exactly one uniform per standard normal — is part of the
 * reproducibility contract: replicate b's draws are a pure function of
 * (seed, b) and the order the caller requests them.
 */
import { normalQuantile } from './distributions.js';
/** FNV-1a 32-bit hash of the seed string, over UTF-16 code units. */
export function hashSeedString(seed) {
    let h = 0x811c9dc5;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
/** splitmix32 finalizer: a strong 32-bit avalanche of the input. */
function splitmix32(z) {
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
    return (z ^ (z >>> 15)) >>> 0;
}
function rotl(x, k) {
    return ((x << k) | (x >>> (32 - k))) >>> 0;
}
/**
 * The xoshiro128** stream for one bootstrap replicate. The state is a
 * pure function of (seed, replicate): two calls with the same
 * arguments produce identical streams, and no other replicate's
 * stream is affected.
 */
export function createReplicateStream(seed, replicate) {
    if (!Number.isInteger(replicate) || replicate < 0) {
        throw new Error(`createReplicateStream: replicate must be a non-negative integer; received ${replicate}.`);
    }
    // splitmix32 expansion of (seedHash, replicate) into the 128-bit
    // state: start from the hash advanced by (replicate + 1) golden
    // increments, then draw four successive splitmix32 outputs.
    let z = (hashSeedString(seed) + Math.imul(replicate + 1, 0x9e3779b9)) | 0;
    const s = new Uint32Array(4);
    for (let k = 0; k < 4; k++) {
        z = (z + 0x9e3779b9) | 0;
        s[k] = splitmix32(z);
    }
    if (s[0] === 0 && s[1] === 0 && s[2] === 0 && s[3] === 0) {
        // xoshiro degenerates at the all-zero state; unreachable from the
        // expansion above in practice, guarded for completeness.
        s[3] = 0x9e3779b9;
    }
    const nextUint32 = () => {
        const result = Math.imul(rotl(Math.imul(s[1], 5), 7), 9) >>> 0;
        const t = (s[1] << 9) | 0;
        s[2] = (s[2] ^ s[0]) >>> 0;
        s[3] = (s[3] ^ s[1]) >>> 0;
        s[1] = (s[1] ^ s[2]) >>> 0;
        s[0] = (s[0] ^ s[3]) >>> 0;
        s[2] = (s[2] ^ t) >>> 0;
        s[3] = rotl(s[3], 11);
        return result;
    };
    const nextUniform = () => nextUint32() / 4294967296;
    const nextNormal = () => {
        // Inversion method (documented in the module header): the +0.5
        // offset keeps the argument strictly inside (0, 1).
        const u = (nextUint32() + 0.5) / 4294967296;
        return normalQuantile(u);
    };
    return { nextUint32, nextUniform, nextNormal };
}
/**
 * Gamma(shape, 1) draw from a stream, by Marsaglia-Tsang (2000) with
 * the shape < 1 boost. Used only by the F-2 model class's parametric
 * bootstrap, which draws replicate effects from the fitted Student-t
 * as a normal scale mixture (bootstrap.ts); the shipped bootstrap
 * path consumes the stream exactly as documented in the module
 * header and never calls this. Consumption is data-dependent but a
 * pure function of the stream state, so replicates stay
 * deterministic.
 */
export function nextGamma(stream, shape) {
    if (!Number.isFinite(shape) || shape <= 0) {
        throw new Error(`nextGamma: shape must be positive and finite; received ${shape}.`);
    }
    if (shape < 1) {
        const u = stream.nextUniform();
        return nextGamma(stream, shape + 1) * Math.pow(u, 1 / shape);
    }
    const d = shape - 1 / 3;
    const c = 1 / Math.sqrt(9 * d);
    for (;;) {
        const x = stream.nextNormal();
        const v0 = 1 + c * x;
        if (v0 <= 0)
            continue;
        const v = v0 * v0 * v0;
        const u = stream.nextUniform();
        if (u < 1 - 0.0331 * (x * x) * (x * x))
            return d * v;
        if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v)))
            return d * v;
    }
}
//# sourceMappingURL=prng.js.map